import { AppSelect } from '../../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, FileText, Loader2, RefreshCw, Search, X } from "lucide-react";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { useTheme } from "../../../context/ThemeContext";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";

const normalizeEpisodeTitle = (episode) => {
  const order = episode?.sortOrder ? `第${episode.sortOrder}集` : "";
  const title = episode?.title || "未命名";
  return order ? `${order} · ${title}` : title;
};

const HighlightedText = ({ text, keyword, accentColor }) => {
  const parts = useMemo(() => {
    const source = text || "";
    const query = (keyword || "").trim();
    if (!query) return [{ text: source, hit: false }];
    const lowerSource = source.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const result = [];
    let cursor = 0;
    let nextIndex = lowerSource.indexOf(lowerQuery, cursor);
    while (nextIndex !== -1) {
      if (nextIndex > cursor) {
        result.push({ text: source.slice(cursor, nextIndex), hit: false });
      }
      result.push({ text: source.slice(nextIndex, nextIndex + query.length), hit: true });
      cursor = nextIndex + query.length;
      nextIndex = lowerSource.indexOf(lowerQuery, cursor);
    }
    if (cursor < source.length) result.push({ text: source.slice(cursor), hit: false });
    return result;
  }, [text, keyword]);

  return (
    <>
      {parts.map((part, index) => part.hit ? (
        <mark
          key={index}
          className="rounded px-0.5"
          style={{ backgroundColor: `${accentColor}33`, color: "var(--color-text-primary)" }}
        >
          {part.text}
        </mark>
      ) : (
        <React.Fragment key={index}>{part.text}</React.Fragment>
      ))}
    </>
  );
};

const SourceTextPanel = ({ isOpen, onClose, text, projectId, episodeId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState(episodeId ? String(episodeId) : "");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [searchText, setSearchText] = useState("");

  const loadEpisodes = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    setLoadError("");
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
      const list = Array.isArray(data) ? data : [];
      setEpisodes(list);
      setSelectedEpisodeId((current) => {
        if (current && list.some((ep) => String(ep.id) === String(current))) return current;
        if (episodeId && list.some((ep) => String(ep.id) === String(episodeId))) return String(episodeId);
        const firstWithScript = list.find((ep) => ep.scriptContent && ep.scriptContent.trim());
        return firstWithScript ? String(firstWithScript.id) : (list[0]?.id ? String(list[0].id) : "");
      });
    } catch (e) {
      setLoadError(e.message || "加载失败");
    } finally {
      setIsLoading(false);
    }
  }, [projectId, episodeId]);

  useEffect(() => {
    if (!isOpen) return;
    loadEpisodes();
  }, [isOpen, loadEpisodes]);

  useEffect(() => {
    if (episodeId) setSelectedEpisodeId(String(episodeId));
  }, [episodeId]);

  const selectedEpisode = useMemo(
    () => episodes.find((ep) => String(ep.id) === String(selectedEpisodeId)),
    [episodes, selectedEpisodeId]
  );

  const sourceText = selectedEpisode ? (selectedEpisode.scriptContent || "") : (text || "");
  const wordCount = sourceText.length;
  const title = selectedEpisode ? normalizeEpisodeTitle(selectedEpisode) : "原小说";
  const hasEpisodes = episodes.length > 0;

  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);
  if (!mounted) return null;

  return (
    <div className="absolute inset-0 z-40 pointer-events-none">
      <aside
        className={`absolute right-0 top-0 h-full w-[calc(100vw_-_64px)] max-w-[520px] md:w-[420px] xl:w-[520px] pointer-events-auto flex flex-col border-l shadow-2xl ${exiting ? "animate-out slide-out-to-right-4 duration-150" : "animate-in slide-in-from-right duration-200"}`}
        style={{
          backgroundColor: "var(--color-bg-secondary)",
          borderColor: "var(--color-border-primary)",
          boxShadow: "var(--shadow-modal)",
        }}
      >
        <div className="shrink-0 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex items-center gap-2">
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${theme.colors.accentPrimary}18`, color: theme.colors.accentPrimary }}
              >
                <BookOpen size={16} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
                  原小说
                </div>
                <div className="text-2xs truncate" style={{ color: "var(--color-text-muted)" }}>
                  {title} · {wordCount.toLocaleString()} 字
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-[var(--color-bg-tertiary)]"
              style={{ color: "var(--color-text-secondary)" }}
              aria-label="关闭原小说"
              title="关闭"
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-4 pb-3 space-y-2">
            <div className="flex items-center gap-2">
              <AppSelect
                value={selectedEpisodeId}
                onChange={(e) => setSelectedEpisodeId(e.target.value)}
                disabled={!hasEpisodes || isLoading}
                className="flex-1 min-w-0 h-9 rounded-lg px-3 text-xs outline-none"
                style={{
                  backgroundColor: "var(--color-bg-primary)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-primary)",
                }}
              >
                {!hasEpisodes && <option value="">当前导入文本</option>}
                {episodes.map((ep) => (
                  <option key={ep.id} value={String(ep.id)}>
                    {normalizeEpisodeTitle(ep)}
                  </option>
                ))}
              </AppSelect>
              <button
                onClick={loadEpisodes}
                disabled={isLoading || !projectId}
                className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 hover:bg-[var(--color-bg-tertiary)]"
                style={{ color: "var(--color-text-secondary)", border: "1px solid var(--color-border-primary)" }}
                aria-label="刷新剧本"
                title="刷新剧本"
              >
                <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
              </button>
            </div>

            <div
              className="h-9 rounded-lg px-3 flex items-center gap-2"
              style={{
                backgroundColor: "var(--color-bg-primary)",
                border: "1px solid var(--color-border-primary)",
              }}
            >
              <Search size={14} style={{ color: "var(--color-text-muted)" }} />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="flex-1 bg-transparent outline-none text-xs"
                style={{ color: "var(--color-text-primary)" }}
                placeholder="搜索原文"
              />
              {searchText && (
                <button
                  onClick={() => setSearchText("")}
                  className="w-6 h-6 rounded flex items-center justify-center transition-colors hover:bg-[var(--color-bg-tertiary)]"
                  style={{ color: "var(--color-text-muted)" }}
                  aria-label="清空搜索"
                  title="清空"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 size={18} className="animate-spin" style={{ color: theme.colors.accentPrimary }} />
            </div>
          ) : loadError ? (
            <div className="m-4 rounded-lg border p-4 text-xs" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
              {loadError}
            </div>
          ) : sourceText ? (
            <pre
              className="px-5 py-4 text-sm leading-7 whitespace-pre-wrap break-words font-sans"
              style={{ color: "var(--color-text-secondary)" }}
            >
              <HighlightedText text={sourceText} keyword={searchText} accentColor={theme.colors.accentPrimary} />
            </pre>
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
              <FileText size={32} style={{ color: "var(--color-text-muted)", opacity: 0.35 }} />
              <div className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>暂无剧本内容</div>
              <div className="text-xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                请先在剧本中心为当前集写入原文。
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default SourceTextPanel;
